import { useMemo } from 'react';

import { useModulesConfig } from '~/data/company';
import {
  EmployeeModuleConfig,
  EmployeeModuleTabConfig,
} from '~/scenes/PortalViews/Views/EmployeeViews/Modules/types';

import {
  filterTabConfigsByEnabledConfigKeys,
  getDefaultTabPath,
  getPathsFromTabConfigs,
} from './data';

type UseEnabledModuleTabPathsOutput<TabPath, TabConfigKey, TabInstructionKey> =
  {
    enabledTabConfigs: EmployeeModuleTabConfig<
      TabPath,
      TabConfigKey,
      TabInstructionKey
    >[];
    enabledTabPaths: TabPath[];
    defaultEnabledTabPath: TabPath | undefined;
    defaultTab: TabPath;
  };

type PropsType<TabPath, TabConfigKey, TabInstructionKey> = {
  availableTabs?: TabPath[];
} & EmployeeModuleConfig<TabPath, TabConfigKey, TabInstructionKey>;

export const useEnabledModuleTabPaths = <
  TabPath,
  TabConfigKey,
  TabInstructionKey,
>(
  props: PropsType<TabPath, TabConfigKey, TabInstructionKey>,
): UseEnabledModuleTabPathsOutput<TabPath, TabConfigKey, TabInstructionKey> => {
  const { tabConfigs, module, defaultTab, availableTabs } = props;

  const { getEnabledTabsForModule, isModuleEnabled } = useModulesConfig();

  const { enabledTabConfigs, enabledTabPaths, defaultEnabledTabPath } =
    useMemo(() => {
      const enabledTabsKeys = getEnabledTabsForModule(module);

      if (!isModuleEnabled(module) || enabledTabsKeys.length === 0) {
        return {
          enabledTabConfigs: [],
          enabledTabPaths: [],
          defaultEnabledTabPath: undefined,
        };
      }

      const availableTabConfigs =
        !!availableTabs && availableTabs.length > 0
          ? tabConfigs.filter((c) => availableTabs.includes(c.path))
          : tabConfigs;

      const enabledTabConfigs = filterTabConfigsByEnabledConfigKeys(
        availableTabConfigs,
        enabledTabsKeys,
      );

      const enabledTabPaths = getPathsFromTabConfigs(enabledTabConfigs);

      const defaultEnabledTabPath = getDefaultTabPath(
        enabledTabPaths,
        defaultTab,
      );

      return {
        enabledTabConfigs,
        enabledTabPaths,
        defaultEnabledTabPath,
      };
    }, [
      module,
      isModuleEnabled,
      getEnabledTabsForModule,
      tabConfigs,
      defaultTab,
      availableTabs,
    ]);

  return {
    enabledTabConfigs,
    enabledTabPaths,
    defaultEnabledTabPath,
    defaultTab: defaultEnabledTabPath ?? defaultTab,
  };
};
